import React, { useRef, useState } from "react";
import {
    motion,
    AnimatePresence,
    useScroll,
    useTransform,
    useMotionValueEvent,
} from "framer-motion";
import {
    Layers, Warehouse, ShoppingBag, ShoppingCart, Wallet, Users, Truck, BarChart3,
    AlertTriangle, TrendingUp, PackageCheck,
} from "lucide-react";
import { Grain, EASE } from "@/components/landing/cinematic";

const SIZE = 800;
const C = SIZE / 2;
const R = 292;

const NODES = [
    { label: "Inventory", icon: Layers },
    { label: "Warehouse", icon: Warehouse },
    { label: "Sales", icon: ShoppingBag },
    { label: "Procurement", icon: ShoppingCart },
    { label: "Finance", icon: Wallet },
    { label: "Customers", icon: Users },
    { label: "Suppliers", icon: Truck },
    { label: "BI", icon: BarChart3 },
];

const CAPTIONS = [
    "Thirteen modules. One live data layer.",
    "Every record flows into YugOS Intelligence.",
    "YugOS Intelligence thinks with your live numbers.",
    "And answers with the next action.",
];

const angleFor = (i) => ((-90 + (360 / NODES.length) * i) * Math.PI) / 180;

/** One orbiting module chip — fades in during phase 0, dims slightly while brain thinks. */
function OrbitNode({ p, i }) {
    const a = angleFor(i);
    const x = 50 + (Math.cos(a) * R * 100) / SIZE;
    const y = 50 + (Math.sin(a) * R * 100) / SIZE;
    const t0 = 0.03 + i * 0.016;
    const opacity = useTransform(p, [t0, t0 + 0.08], [0, 1]);
    const scale = useTransform(p, [t0, t0 + 0.08], [0.7, 1]);
    const Icon = NODES[i].icon;
    return (
        <div
            className="absolute z-10 -translate-x-1/2 -translate-y-1/2"
            style={{ left: `${x}%`, top: `${y}%` }}
        >
            <motion.div
                data-testid={`brain-node-${NODES[i].label.toLowerCase()}`}
                style={{ opacity, scale }}
            >
            <div className="flex items-center gap-1.5 rounded-full border border-[#f7f3ec]/15 bg-[#1d1a16]/90 px-2.5 py-1.5 text-[11px] font-medium text-[#e8e2d6] shadow-[0_10px_30px_-10px_rgba(0,0,0,0.7)] backdrop-blur whitespace-nowrap sm:px-3 sm:text-[12px]">
                <Icon className="h-3 w-3 text-[#8b96f8]" strokeWidth={1.6} />
                {NODES[i].label}
            </div>
            </motion.div>
        </div>
    );
}

/** Connector spoke that draws toward the brain + a data pulse traveling it. */
function Spoke({ p, i }) {
    const a = angleFor(i);
    const x = C + Math.cos(a) * (R - 14);
    const y = C + Math.sin(a) * (R - 14);
    const t0 = 0.2 + i * 0.02;
    const pathLength = useTransform(p, [t0, t0 + 0.16], [0, 1]);
    const dotOpacity = useTransform(p, [t0 + 0.14, t0 + 0.2], [0, 1]);
    return (
        <g>
            <motion.line
                x1={x} y1={y} x2={C} y2={C}
                stroke="url(#brain-spoke)"
                strokeWidth="1.6"
                style={{ pathLength, opacity: pathLength }}
            />
            <motion.circle r="3" fill="#8b96f8" style={{ opacity: dotOpacity }}>
                <animateMotion
                    dur={`${2.4 + i * 0.35}s`}
                    repeatCount="indefinite"
                    path={`M ${x} ${y} L ${C} ${C}`}
                />
            </motion.circle>
        </g>
    );
}

/** Insight card that springs in during the answer phase. */
function InsightCard({ p, window: [t0, t1], className, tone, icon: Icon, title, line, testId }) {
    const opacity = useTransform(p, [t0, t1], [0, 1]);
    const yv = useTransform(p, [t0, t1], [26, 0]);
    const scale = useTransform(p, [t0, t1], [0.92, 1]);
    return (
        <div className={`absolute z-20 w-[168px] sm:w-[200px] ${className}`}>
            <motion.div
                data-testid={testId}
                style={{ opacity, y: yv, scale }}
            >
            <div className="rounded-xl border border-[#f7f3ec]/12 bg-[#1d1a16]/95 p-3 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.8)] backdrop-blur">
                <div className={`flex items-center gap-1.5 text-[11px] font-semibold ${tone}`}>
                    <Icon className="h-3 w-3" strokeWidth={2} />
                    {title}
                </div>
                <div className="mt-1 text-[11px] leading-snug text-[#a29a8e]">{line}</div>
            </div>
            </motion.div>
        </div>
    );
}

export default function Brain() {
    const ref = useRef(null);
    const { scrollYProgress: p } = useScroll({ target: ref, offset: ["start start", "end end"] });
    const [phase, setPhase] = useState(0);

    useMotionValueEvent(p, "change", (v) => {
        const next = v < 0.22 ? 0 : v < 0.5 ? 1 : v < 0.72 ? 2 : 3;
        setPhase((prev) => (prev === next ? prev : next));
    });

    const ringOpacity = useTransform(p, [0.02, 0.14], [0, 1]);
    const coreScale = useTransform(p, [0.02, 0.18], [0.78, 1]);
    const coreOpacity = useTransform(p, [0.02, 0.14], [0, 1]);
    const pulseOpacity = useTransform(p, [0.42, 0.5, 0.88, 1], [0, 1, 1, 0.6]);
    const thinkOpacity = useTransform(p, [0.44, 0.5, 0.68, 0.74], [0, 1, 1, 0]);
    const answerOpacity = useTransform(p, [0.78, 0.88], [0, 1]);
    const answerY = useTransform(p, [0.78, 0.88], [16, 0]);

    return (
        <>
            {/* MOBILE — static, compact, non-scroll-driven; keeps the story without the 300vh sticky cost */}
            <BrainMobile />

            {/* DESKTOP — the cinematic sticky orbit */}
            <section
                id="brain"
                ref={ref}
                className="relative hidden lg:block h-[210vh] bg-[#151310] text-[#f3efe7]"
                data-testid="brain-centerpiece"
            >
            <div className="sticky top-0 flex h-screen w-full flex-col items-center justify-center overflow-hidden px-6">
                <Grain opacity={0.07} />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(67,83,228,0.14),transparent_58%)] pointer-events-none" aria-hidden="true" />

                {/* Headline + caption — sits just above the stage as part of the centered composition */}
                <div className="relative z-30 text-center max-w-4xl">
                    <h2 className="font-display text-4xl xl:text-[52px] font-bold tracking-[-0.03em] text-white leading-[1.03]">
                        An AI Operating System.{" "}
                        <span className="font-editorial font-medium text-[#a29a8e]">Not another ERP.</span>
                    </h2>
                    <div className="mt-3 h-6" data-testid="brain-caption">
                        <AnimatePresence mode="wait">
                            <motion.p
                                key={phase}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.36, ease: EASE }}
                                className="font-mono text-[12.5px] uppercase tracking-[0.2em] text-[#8b96f8]"
                            >
                                {CAPTIONS[phase]}
                            </motion.p>
                        </AnimatePresence>
                    </div>
                </div>

                {/* The stage — bigger (up to 780px), sits directly below the headline */}
                <div className="relative z-20 mt-6 xl:mt-8 w-[min(78vh,780px)] aspect-square">
                    <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="absolute inset-0 h-full w-full overflow-visible" aria-hidden="true">
                        <defs>
                            <linearGradient id="brain-spoke" x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stopColor="rgba(139,150,248,0.85)" />
                                <stop offset="100%" stopColor="rgba(139,150,248,0.28)" />
                            </linearGradient>
                            <radialGradient id="brain-core" cx="50%" cy="50%" r="50%">
                                <stop offset="0%" stopColor="rgba(67,83,228,0.5)" />
                                <stop offset="100%" stopColor="rgba(21,19,16,0)" />
                            </radialGradient>
                        </defs>

                        {/* Orbit rings — slow ambient rotation */}
                        <motion.g style={{ opacity: ringOpacity }}>
                            <g className="animate-orbit">
                                <circle cx={C} cy={C} r={R} fill="none" stroke="rgba(247,243,236,0.22)" strokeWidth="1" strokeDasharray="3 7" />
                            </g>
                            <circle cx={C} cy={C} r={R - 74} fill="none" stroke="rgba(247,243,236,0.10)" strokeWidth="1" strokeDasharray="2 6" />
                        </motion.g>

                        {/* Connector spokes + data pulses */}
                        {NODES.map((_, i) => (
                            <Spoke key={i} p={p} i={i} />
                        ))}

                        {/* Thinking halo */}
                        <motion.g style={{ opacity: pulseOpacity }}>
                            <circle cx={C} cy={C} r="120" fill="url(#brain-core)" />
                            <motion.circle
                                cx={C} cy={C} fill="none" stroke="rgba(139,150,248,0.4)" strokeWidth="1"
                                initial={{ r: 92, opacity: 0.6 }}
                                animate={{ r: [92, 150], opacity: [0.55, 0] }}
                                transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut" }}
                            />
                            <motion.circle
                                cx={C} cy={C} fill="none" stroke="rgba(139,150,248,0.3)" strokeWidth="1"
                                initial={{ r: 92, opacity: 0.5 }}
                                animate={{ r: [92, 170], opacity: [0.45, 0] }}
                                transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut", delay: 1.1 }}
                            />
                        </motion.g>
                    </svg>

                    {/* Center — Yug Brain core (flex wrapper does centering; motion only scales/fades) */}
                    <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
                        <motion.div
                            className="text-center"
                            style={{ scale: coreScale, opacity: coreOpacity }}
                            data-testid="brain-core"
                        >
                        <div className="flex flex-col items-center rounded-2xl border border-[#8b96f8]/25 bg-[#1d1a16]/90 px-6 py-5 shadow-[0_0_100px_-14px_rgba(67,83,228,0.6)] backdrop-blur">
                            <img
                                src="/brand/yugos-logo.png"
                                alt=""
                                width={48}
                                height={48}
                                draggable={false}
                                className="select-none drop-shadow-[0_0_16px_rgba(139,150,248,0.75)]"
                                style={{ filter: "brightness(1.35) drop-shadow(0 0 14px rgba(139,150,248,0.6))" }}
                            />
                            <div className="mt-2.5 font-display text-[18px] font-bold tracking-tight text-white leading-tight">YugOS Intelligence</div>
                            <div className="mt-0.5 font-mono text-[9.5px] uppercase tracking-[0.24em] text-[#8b96f8]">AI copilot</div>
                        </div>
                        <motion.div
                            style={{ opacity: thinkOpacity }}
                            className="absolute left-1/2 -translate-x-1/2 top-full mt-3.5 whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.22em] text-[#8b96f8]"
                        >
                            thinking
                            <motion.span animate={{ opacity: [0, 1, 0] }} transition={{ duration: 1.2, repeat: Infinity }}>…</motion.span>
                        </motion.div>
                    </motion.div>
                    </div>

                    {/* Orbiting module chips */}
                    {NODES.map((_, i) => (
                        <OrbitNode key={i} p={p} i={i} />
                    ))}

                    {/* Insights converge — symmetric offsets around the stage. Tighter windows for a snappier payoff */}
                    <InsightCard
                        p={p} window={[0.5, 0.6]}
                        className="left-[-6%] top-[6%] xl:-left-24"
                        tone="text-amber-400" icon={AlertTriangle}
                        title="Stockout risk" line="PVC Pipe 25mm · ~4 days of stock left"
                        testId="brain-insight-stockout"
                    />
                    <InsightCard
                        p={p} window={[0.58, 0.68]}
                        className="right-[-6%] top-[6%] xl:-right-24"
                        tone="text-red-400" icon={Wallet}
                        title="₹2.84L overdue" line="Sharma Industries · 12 days past due"
                        testId="brain-insight-overdue"
                    />
                    <InsightCard
                        p={p} window={[0.66, 0.76]}
                        className="bottom-[-4%] left-1/2 -translate-x-1/2"
                        tone="text-emerald-400" icon={PackageCheck}
                        title="Reorder now" line="OPC Cement 53 · 200 bags suggested"
                        testId="brain-insight-reorder"
                    />
                </div>

                {/* Final answer line — sits just below the stage as part of the centered composition */}
                <motion.div
                    style={{ opacity: answerOpacity, y: answerY }}
                    className="relative z-30 mt-8 xl:mt-10 text-center pointer-events-none"
                    data-testid="brain-answer"
                >
                    <div className="inline-flex items-center gap-2 rounded-full border border-[#f7f3ec]/12 bg-[#1d1a16]/80 px-4 py-2 text-[13.5px] text-[#e8e2d6] backdrop-blur">
                        <TrendingUp className="h-3.5 w-3.5 text-[#8b96f8]" strokeWidth={2} />
                        Ask anything. Grounded in your data — next action ready.
                    </div>
                </motion.div>
            </div>
            </section>
        </>
    );
}

/** Mobile Brain — a static, compact rendering that tells the same story without the 300vh sticky cost. */
function BrainMobile() {
    return (
        <section
            id="brain"
            className="relative lg:hidden bg-[#151310] text-[#f3efe7] px-6 py-24"
            data-testid="brain-centerpiece-mobile"
        >
            <Grain opacity={0.07} />
            <div
                className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_38%,rgba(67,83,228,0.16),transparent_60%)]"
                aria-hidden="true"
            />

            <div className="relative">
                <div className="font-mono text-[11px] font-medium uppercase tracking-[0.24em] text-[#8b96f8]">
                    YugOS Intelligence · AI copilot
                </div>
                <h2 className="mt-4 font-display text-[32px] font-bold tracking-[-0.03em] text-white leading-[1.06]">
                    An AI Operating System.{" "}
                    <span className="font-editorial font-medium text-[#a29a8e]">Not another ERP.</span>
                </h2>
                <p className="mt-4 max-w-md text-[14px] leading-relaxed text-[#a29a8e]">
                    Every module feeds one live data layer. YugOS Intelligence thinks with your real numbers and answers with the next action.
                </p>

                {/* Compact orbit — square SVG, fully centered, no scroll dependency */}
                <div className="relative mx-auto mt-10 aspect-square w-full max-w-[320px]">
                    <svg viewBox="0 0 800 800" className="absolute inset-0 h-full w-full" aria-hidden="true">
                        <defs>
                            <linearGradient id="brain-spoke-m" x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stopColor="rgba(139,150,248,0.85)" />
                                <stop offset="100%" stopColor="rgba(139,150,248,0.22)" />
                            </linearGradient>
                        </defs>
                        <circle cx={400} cy={400} r={292} fill="none" stroke="rgba(247,243,236,0.20)" strokeWidth="1.2" strokeDasharray="3 7" />
                        <circle cx={400} cy={400} r={218} fill="none" stroke="rgba(247,243,236,0.10)" strokeWidth="1" strokeDasharray="2 6" />
                        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
                            const a = ((-90 + (360 / 8) * i) * Math.PI) / 180;
                            const x = 400 + Math.cos(a) * (292 - 14);
                            const y = 400 + Math.sin(a) * (292 - 14);
                            return <line key={i} x1={x} y1={y} x2={400} y2={400} stroke="url(#brain-spoke-m)" strokeWidth="1.4" opacity="0.7" />;
                        })}
                        <circle cx={400} cy={400} r="110" fill="url(#brain-core-m)" />
                        <defs>
                            <radialGradient id="brain-core-m" cx="50%" cy="50%" r="50%">
                                <stop offset="0%" stopColor="rgba(67,83,228,0.5)" />
                                <stop offset="100%" stopColor="rgba(21,19,16,0)" />
                            </radialGradient>
                        </defs>
                    </svg>

                    {/* 8 orbit chips */}
                    {[
                        { label: "Inventory", Icon: Layers },
                        { label: "Warehouse", Icon: Warehouse },
                        { label: "Sales", Icon: ShoppingBag },
                        { label: "Procurement", Icon: ShoppingCart },
                        { label: "Finance", Icon: Wallet },
                        { label: "Customers", Icon: Users },
                        { label: "Suppliers", Icon: Truck },
                        { label: "BI", Icon: BarChart3 },
                    ].map(({ label, Icon }, i) => {
                        const a = ((-90 + (360 / 8) * i) * Math.PI) / 180;
                        const x = 50 + (Math.cos(a) * 292 * 100) / 800;
                        const y = 50 + (Math.sin(a) * 292 * 100) / 800;
                        return (
                            <div
                                key={label}
                                className="absolute -translate-x-1/2 -translate-y-1/2"
                                style={{ left: `${x}%`, top: `${y}%` }}
                                data-testid={`brain-node-mobile-${label.toLowerCase()}`}
                            >
                                <div className="flex items-center gap-1 rounded-full border border-[#f7f3ec]/15 bg-[#1d1a16]/90 px-2 py-1 text-[10px] font-medium text-[#e8e2d6] shadow-[0_10px_24px_-10px_rgba(0,0,0,0.6)] backdrop-blur whitespace-nowrap">
                                    <Icon className="h-2.5 w-2.5 text-[#8b96f8]" strokeWidth={1.6} />
                                    {label}
                                </div>
                            </div>
                        );
                    })}

                    {/* Center core */}
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="flex flex-col items-center rounded-2xl border border-[#8b96f8]/30 bg-[#1d1a16]/95 px-4 py-3 shadow-[0_0_60px_-12px_rgba(67,83,228,0.55)] backdrop-blur" data-testid="brain-core-mobile">
                            <img
                                src="/brand/yugos-logo.png"
                                alt=""
                                width={32}
                                height={32}
                                draggable={false}
                                className="select-none drop-shadow-[0_0_10px_rgba(139,150,248,0.6)]"
                                style={{ filter: "brightness(1.3)" }}
                            />
                            <div className="mt-1.5 font-display text-[13px] font-bold tracking-tight text-white leading-tight">YugOS Intelligence</div>
                            <div className="mt-0.5 font-mono text-[8.5px] uppercase tracking-[0.22em] text-[#8b96f8]">AI copilot</div>
                        </div>
                    </div>
                </div>

                {/* 3 insight cards — horizontal snap carousel below the orbit */}
                <div className="mt-8 -mx-6">
                    <div className="snap-x-strip flex gap-3 overflow-x-auto px-6 pb-2" data-testid="brain-insights-mobile">
                        {[
                            { tone: "text-amber-400", Icon: AlertTriangle, title: "Stockout risk", line: "PVC Pipe 25mm · ~4 days of stock left", tid: "brain-insight-mobile-stockout" },
                            { tone: "text-red-400", Icon: Wallet, title: "₹2.84L overdue", line: "Sharma Industries · 12 days past due", tid: "brain-insight-mobile-overdue" },
                            { tone: "text-emerald-400", Icon: PackageCheck, title: "Reorder now", line: "OPC Cement 53 · 200 bags suggested", tid: "brain-insight-mobile-reorder" },
                        ].map((c) => (
                            <div
                                key={c.title}
                                data-testid={c.tid}
                                className="w-[220px] shrink-0 rounded-xl border border-[#f7f3ec]/12 bg-[#1d1a16]/95 p-3.5 shadow-[0_20px_40px_-20px_rgba(0,0,0,0.7)]"
                            >
                                <div className={`flex items-center gap-1.5 text-[11.5px] font-semibold ${c.tone}`}>
                                    <c.Icon className="h-3 w-3" strokeWidth={2} />
                                    {c.title}
                                </div>
                                <div className="mt-1.5 text-[11.5px] leading-snug text-[#a29a8e]">{c.line}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="mt-8 flex items-center justify-center">
                    <div className="inline-flex items-center gap-2 rounded-full border border-[#f7f3ec]/12 bg-[#1d1a16]/80 px-3.5 py-2 text-[12.5px] text-[#e8e2d6]">
                        <TrendingUp className="h-3.5 w-3.5 text-[#8b96f8]" strokeWidth={2} />
                        Ask anything. Grounded in your data.
                    </div>
                </div>
            </div>
        </section>
    );
}
